import { useMemo, useContext } from "react";
import { StyleSheet, View, Text } from "react-native";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

import { appColors, tooltipShadow } from "../constants/colors";
import { CalculationsContext } from "../store/calculationsCtx";
import ChartLayout from "./UI/ChartLayout";

const RADIAN = Math.PI / 180;

const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.04) return null; // hide label if slice is too small
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight="500">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { name, value, formattedValue } = payload[0].payload;
  return (
    <View style={tooltip.box}>
      <Text style={tooltip.name}>{name}</Text>
      <Text style={tooltip.value}>{formattedValue}</Text>
    </View>
  );
};

const tooltip = StyleSheet.create({
  box: {
    backgroundColor: appColors.cardBg,
    borderRadius: 8,
    borderWidth: 0.5,
    borderColor: appColors.border,
    paddingVertical: 6,
    paddingHorizontal: 10,
    ...tooltipShadow,
  },
  name: { fontSize: 11, color: appColors.textSecondary, marginBottom: 2 },
  value: { fontSize: 12, fontWeight: "500", color: appColors.textPrimary },
});

function CustomPieChart() {
  const calculationsCtx = useContext(CalculationsContext);
  const { results2 } = calculationsCtx;

  const chartData = useMemo(() => {
    const interestValue = +results2.interest2 || 0;
    const loanValue = +results2.loan2 || 0;
    const fees = +results2.feesNumber || 0;
    const balloon = +results2.balloon || 0;

    const entries = [
      { name: "Principal", value: loanValue, formattedValue: results2.loan, color: appColors.chartPrincipal },
      { name: "Interest", value: interestValue, formattedValue: results2.interest, color: appColors.chartInterest },
    ];
    if (fees > 0)
      entries.push({ name: "Monthly fees", value: fees, formattedValue: results2.fees, color: appColors.chartFees });
    if (balloon > 0)
      entries.push({
        name: "Balloon",
        value: balloon,
        formattedValue: results2.balloonValue,
        color: appColors.chartBalloon,
      });

    return entries;
  }, [results2.interest2, results2.loan2, results2.total2, results2.feesNumber, results2.balloon]);

  const total = chartData.reduce((s, d) => s + d.value, 0);

  return (
    <ChartLayout>
      <View style={styles.header}>
        <Text style={styles.title}>Cost breakdown</Text>
      </View>

      {/* Legend */}
      <View style={styles.legend}>
        {chartData.map(item => (
          <View key={item.name} style={styles.legendItem}>
            <View style={[styles.swatch, { backgroundColor: item.color }]} />
            <Text style={styles.legendLabel}>{item.name}</Text>
            <Text style={styles.legendValue}>{item.formattedValue}</Text>
          </View>
        ))}
      </View>

      {/* Donut chart */}
      <View style={styles.chartWrap}>
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius="52%"
              outerRadius="78%"
              dataKey="value"
              labelLine={false}
              label={renderCustomLabel}
              strokeWidth={0}
            >
              {chartData.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>

        {/* Centre label */}
        <View style={[styles.centreLabel, { pointerEvents: "none" }]}>
          <Text style={styles.centreSub}>Total paid</Text>
          <Text style={styles.centreValue}>{results2.total}</Text>
        </View>
      </View>
    </ChartLayout>
  );
}

export default CustomPieChart;

const styles = StyleSheet.create({
  header: {
    width: "100%",
    marginBottom: 8,
  },

  title: {
    fontSize: 12,
    fontWeight: "500",
    color: appColors.textPrimary,
  },

  legend: {
    width: "100%",
    gap: 6,
    marginBottom: 4,
  },

  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },

  swatch: {
    width: 10,
    height: 10,
    borderRadius: 3,
  },

  legendLabel: {
    flex: 1,
    fontSize: 11,
    color: appColors.textSecondary,
  },

  legendValue: {
    fontSize: 11,
    fontWeight: "500",
    color: appColors.textPrimary,
  },

  chartWrap: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },

  centreLabel: {
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },

  centreSub: {
    fontSize: 10,
    color: appColors.textSecondary,
    marginBottom: 2,
  },

  centreValue: {
    fontSize: 13,
    fontWeight: "500",
    color: appColors.primary,
  },
});
